import { useQuery } from 'react-query';
import { openerOrchestratorAxiosApi } from 'src/Config/axiosApi';

/**
 * Cross-group opener descalce snapshot — read from
 * `manual-order-monitor`'s sweeper via `opener-orchestrator`'s
 * `GET /positions/opener-descalce` (a single Redis GET, no Mongo, no venue
 * call). Poll pattern copied from `api/useOpenerGroupPods.ts`.
 *
 * Served by `openerOrchestratorAxiosApi` (`VITE_OPENER_ORCHESTRATOR_URL`) per
 * the control-plane domain split — the descalce screen is an opener-domain
 * read. The route string is byte-identical (`/positions/opener-descalce`);
 * only the base URL changed. `prediction-arbitrage-manager` no longer serves
 * this route at all, so pointing back at it 404s the whole screen.
 *
 * Wire contract per the approved spec/plan (§3.2). `ruleStatus` is typed as
 * plain `string`, NOT a union: the live `entry_rules` collection only ever
 * carries `active` / `paused` today, and typing a union the DB doesn't back
 * would let the FE assume `completed` / `cancelled` rows can't appear.
 */

export type OpenerDescalcePlatform = 'kalshi' | 'polymarket';

export type OpenerDescalceSource =
  | 'venue'
  | 'starting'
  | 'unconfigured'
  | 'error';

/**
 * One venue-account the sweeper read. The snapshot carries one entry PER
 * VENUE-ACCOUNT — `accountId` is unique per platform, never across, so an
 * entry is only identified by the `(platform, accountId)` pair.
 */
export interface OpenerDescalceAccountMeta {
  platform: OpenerDescalcePlatform;
  accountId: string;
  source: OpenerDescalceSource;
  lastSuccessAt: number | null;
  lastError: string | null;
  /** Always `0` on a Kalshi entry — redemption is a Polymarket concept. */
  redeemableExcluded: number;
}

/** The two venue-accounts a row's positions were read under. */
export interface OpenerDescalceAccountPair {
  kalshiAccountId: string;
  polymarketAccountId: string;
}

export interface OpenerDescalceGroup {
  id: string | null;
  name: string | null;
  podsDesired: 'on' | 'off' | 'panicked' | null;
  status: string | null;
  /** `null` when the row is ungrouped, or the group names only one venue. */
  accounts: OpenerDescalceAccountPair | null;
  eventFinished: boolean;
}

/**
 * Every count is `null` when its leg was NOT read — see `legsRead`. A `null`
 * is "we do not know", which is a different claim from `0` ("the account
 * holds nothing"), and the two must never render the same on a screen whose
 * only job is finding naked legs.
 */
export interface OpenerDescalceVenue {
  polyYes: number | null;
  polyNo: number | null;
  kalshiYes: number | null;
  kalshiNo: number | null;
  /** `+` = excess YES. `null` unless BOTH legs were read. */
  signedNet: number | null;
  /** `|signedNet|`. `null` unless BOTH legs were read. */
  descalce: number | null;
  /** More than one rule claims one of this row's markets. */
  ambiguous: boolean;
  /** A leg already resolved at its venue (Kalshi settled it, or Polymarket
   *  flagged it redeemable) — a settlement artefact, not a naked leg. */
  resolving: boolean;
  /** Which venue resolved; `null` when `resolving` is false. Optional: rows
   *  from a backend older than the Kalshi-settlement read omit it. */
  resolvingVenue?: 'kalshi' | 'polymarket' | 'both' | null;
  /** This rule is missing a ticker or a Polymarket leg — the counts above
   *  necessarily come from one venue only. Optional: only rows built from a
   *  live backend snapshot carry it; keep it optional so existing fixtures
   *  that predate this field don't need updating. */
  marketsIncomplete?: boolean;
}

export interface OpenerDescalceBelief {
  kalshiNet: number;
  polyNet: number;
  descalce: number;
  /** Producer (engine) clock — passed through verbatim, never re-stamped. */
  at: number;
}

export interface OpenerDescalceDivergence {
  total: number;
  kalshi: number;
  poly: number;
}

export interface OpenerDescalceRow {
  ruleId: string;
  pairId: string;
  pairName: string;
  ruleStatus: string;
  direction: 'entry';
  /** The venue-accounts this row's counts were read from. */
  accounts: OpenerDescalceAccountPair;
  /** Which legs the sweep actually reached. A `false` here is why the
   *  matching `venue` counts are `null`. */
  legsRead: { kalshi: boolean; polymarket: boolean };

  group: OpenerDescalceGroup;
  subgroupId: string | null;

  markets: {
    kalshiTicker: string | null;
    polyYesTokenId: string | null;
    polyNoTokenId: string | null;
  };

  /** `null` when this row's accounts are unreadable. */
  venue: OpenerDescalceVenue | null;
  /** `null` when the engine has published nothing for this rule. */
  belief: OpenerDescalceBelief | null;
  /** `null` whenever `belief` is `null`, and whenever a leg went unread. */
  divergence: OpenerDescalceDivergence | null;

  maxUnhedgedContracts: number | null;
  hedgeCheckEnabled: boolean;
}

export interface OpenerDescalceSnapshot {
  /** epoch ms, sweeper clock. */
  computedAt: number;
  /** so the FE can age it. */
  pollMs: number;
  /** freshness horizon. */
  staleMs: number;
  /** One entry per venue-account, sorted by platform then `accountId`. */
  accounts: OpenerDescalceAccountMeta[];
  /** Rules the sweeper REFUSED to render because their account pair could not
   *  be resolved. Nothing else on the screen accounts for them, so leaving
   *  them unreported makes them invisible. */
  unresolvedAccounts: { ruleCount: number; groupIds: string[] };
  rows: OpenerDescalceRow[];
}

/**
 * Stable key for one venue-account. `accountId` is unique PER PLATFORM, so the
 * platform has to be part of the key — the same slug can name a different
 * account on the other venue.
 */
export function venueAccountKey(
  platform: OpenerDescalcePlatform,
  accountId: string
): string {
  return `${platform}:${accountId}`;
}

/**
 * The id used when a payload carries no usable account pair. It can never
 * collide with a registered account (`accountId` is a DNS label the registry
 * owns), so such a row is matched by no account filter: it stays visible under
 * the default empty selection and disappears the moment the operator scopes to
 * a real account.
 */
export const UNKNOWN_ACCOUNT_ID = 'unknown';

const UNKNOWN_ACCOUNT_PAIR: OpenerDescalceAccountPair = {
  kalshiAccountId: UNKNOWN_ACCOUNT_ID,
  polymarketAccountId: UNKNOWN_ACCOUNT_ID
};

export function isAccountPair(
  value: unknown
): value is OpenerDescalceAccountPair {
  const pair = value as OpenerDescalceAccountPair | null | undefined;
  return (
    pair != null &&
    typeof pair.kalshiAccountId === 'string' &&
    typeof pair.polymarketAccountId === 'string'
  );
}

/**
 * A SHAPE fallback, not a data one: the wire before venue accounts carried
 * `account: 'prod' | 'test'` and no pair at all, so every read of
 * `row.accounts.*` is a `TypeError` on a mixed deploy — and with no
 * ErrorBoundary in the app that blanks the whole SPA, not just this screen.
 * The placeholder names no account and asserts nothing about the position.
 */
export function rowAccounts(
  row: Pick<OpenerDescalceRow, 'accounts'>
): OpenerDescalceAccountPair {
  return isAccountPair(row.accounts) ? row.accounts : UNKNOWN_ACCOUNT_PAIR;
}

/** The same shape fallback for a pair that may come from the group or the row. */
export function accountPairOrUnknown(
  value: unknown
): OpenerDescalceAccountPair {
  return isAccountPair(value) ? value : UNKNOWN_ACCOUNT_PAIR;
}

/**
 * The fleet as an ARRAY, whatever the payload holds. `?? []` is not enough:
 * the legacy wire sent a Record keyed by account name, and `{}.map` throws.
 * Deliberately NOT normalised into entries — inventing `platform` values from
 * a shape we no longer speak would put made-up accounts on the strip.
 */
export function snapshotAccounts(
  snapshot: Pick<OpenerDescalceSnapshot, 'accounts'> | undefined
): OpenerDescalceAccountMeta[] {
  return Array.isArray(snapshot?.accounts) ? snapshot!.accounts : [];
}

/** The two account keys a row was read under, Kalshi first. */
export function rowAccountKeys(row: OpenerDescalceRow): string[] {
  const accounts = rowAccounts(row);
  return [
    venueAccountKey('kalshi', accounts.kalshiAccountId),
    venueAccountKey('polymarket', accounts.polymarketAccountId)
  ];
}

/**
 * The one gate both transports use. Shared on purpose: the WS used to drop a
 * payload the REST path accepted verbatim, so a backend that stopped sending
 * `staleMs` would kill the push entirely (every frame dropped) while the poll
 * kept feeding the page the same payload and `useDescalceStale` returned false
 * forever — the "old rendered as fresh" failure the guard exists to prevent,
 * not prevented.
 *
 * Each field is load-bearing for a DIFFERENT failure:
 *  · `computedAt` — the staleness banner ages off it; absent, nothing is ever
 *    stale;
 *  · `rows` — `resolveDescalceViewState` reads `.length` and throws without it;
 *  · `accounts` with at least one entry — an EMPTY fleet degrades
 *    `unconfigured`/`error` straight into `empty`, the screen asserting "no
 *    naked legs" over a payload that said no such thing. Truthiness is not
 *    enough here: `JSON.stringify` drops `undefined` values silently, which is
 *    exactly how a `{}` would reach the wire. Counted with `Object.keys`, NOT
 *    `Array.isArray`, and deliberately so: the pre-venue-accounts backend
 *    still publishes a Record keyed by account name, and refusing that shape
 *    here would take the entire naked-leg screen to an error state for the
 *    whole mixed-deploy window. The shape is absorbed downstream by
 *    `snapshotAccounts`, and the "an unreadable fleet must never render as
 *    `empty`" claim is enforced where the claim is actually made, in
 *    `resolveDescalceViewState`;
 *  · `staleMs > 0` — three guarantees at once: the Stale banner, and
 *    `isDataEqual`'s horizon escape (which computes against `NaN` without it,
 *    so the cache can stick to one snapshot for the life of the tab).
 *
 * `pollMs` is deliberately NOT required. Nothing on the render path reads it —
 * only the socket hook, to size its push window — so rejecting a snapshot over
 * it would take the whole screen to an error state for a field the screen does
 * not use. The socket falls back to a default instead, which is the
 * proportionate response to a field only IT consumes.
 */
export function isUsableDescalceSnapshot(
  snapshot: unknown
): snapshot is OpenerDescalceSnapshot {
  const s = snapshot as Partial<OpenerDescalceSnapshot> | null | undefined;
  return (
    !!s &&
    typeof s.computedAt === 'number' &&
    Array.isArray(s.rows) &&
    !!s.accounts &&
    Object.keys(s.accounts).length > 0 &&
    typeof s.staleMs === 'number' &&
    s.staleMs > 0
  );
}

/** Thrown when a 200 carries a payload the page cannot safely render. */
export class OpenerDescalceMalformedSnapshotError extends Error {
  constructor() {
    super('opener-descalce snapshot is missing a required field');
    this.name = 'OpenerDescalceMalformedSnapshotError';
  }
}

export const OPENER_DESCALCE_SNAPSHOT_UNAVAILABLE_REASON =
  'snapshot-unavailable';

/**
 * Thrown by `fetchOpenerDescalce` when the backend 404s with
 * `{ reason: 'snapshot-unavailable' }` — the sweeper hasn't published yet, or
 * its key expired. Distinguished from every OTHER fetch failure so the page
 * can render the named "snapshot unavailable" state instead of a generic
 * error (never a silent FE fallback to another source).
 */
export class OpenerDescalceSnapshotUnavailableError extends Error {
  constructor() {
    super('opener-descalce snapshot unavailable');
    this.name = 'OpenerDescalceSnapshotUnavailableError';
  }
}

export function isSnapshotUnavailableError(error: unknown): boolean {
  return error instanceof OpenerDescalceSnapshotUnavailableError;
}

export const OPENER_DESCALCE_KEY = ['opener-descalce-snapshot'];

export async function fetchOpenerDescalce(): Promise<OpenerDescalceSnapshot> {
  try {
    const { data } =
      await openerOrchestratorAxiosApi.get<OpenerDescalceSnapshot>(
        '/positions/opener-descalce'
      );
    // Fail LOUD rather than render it. A snapshot missing one of these makes
    // the page lie quietly — stale data with no Stale banner — and this screen
    // would rather show an error than a confident wrong answer.
    if (!isUsableDescalceSnapshot(data)) {
      throw new OpenerDescalceMalformedSnapshotError();
    }
    return data;
  } catch (err) {
    const axiosErr = err as {
      response?: { status?: number; data?: { reason?: string } };
    };
    if (
      axiosErr.response?.status === 404 &&
      axiosErr.response?.data?.reason ===
        OPENER_DESCALCE_SNAPSHOT_UNAVAILABLE_REASON
    ) {
      throw new OpenerDescalceSnapshotUnavailableError();
    }
    throw err;
  }
}

/**
 * How far ahead of our own clock a snapshot may be stamped before we stop
 * believing it is "newer". Producer and browser clocks drift, and the sweep is
 * 15s, so a minute is generous for honest skew and short enough that a genuine
 * clock fault resolves itself on the next read.
 */
export const CLOCK_SKEW_ALLOWANCE_MS = 60_000;

/** Cadence while the `/descalce` socket is delivering: a safety net only. */
export const DESCALCE_POLL_MS_PUSH_ACTIVE = 60_000;

/** Cadence with no push — the sole transport, so it stays under the 15s sweep. */
export const DESCALCE_POLL_MS_NO_PUSH = 5_000;

export interface UseOpenerDescalceOptions {
  /**
   * The `/descalce` socket has delivered a frame recently
   * (`useOpenerDescalceSocket().pushActive`). Defaults to `false` — a caller
   * that does not wire the socket up keeps the original 5s poll and the
   * screen behaves exactly as it did before the push transport existed.
   */
  pushActive?: boolean;
}

/**
 * The snapshot query. Owns the cache slot the `/descalce` socket writes into,
 * so the page reads ONE piece of state no matter which transport filled it.
 *
 * Cadence follows the push, it does not race it. While frames are arriving the
 * REST read drops to a slow safety net; the instant they stop (gateway pod
 * rolling, socket silently half-open, sweeper dead) it returns to 5s — under
 * the 15s sweep, so nothing is missed. `retry: false` so a 404 (the expected
 * pre-first-sweep state) renders immediately instead of retrying three times.
 *
 * 🚨 The poll is never removed outright. Redis pub/sub has no retention and no
 * delivery guarantee, and on THIS screen an empty table asserts "no naked
 * legs" — a transport that can go quiet without saying so must have a second
 * one behind it.
 */
export function useOpenerDescalce(options: UseOpenerDescalceOptions = {}) {
  const { pushActive = false } = options;
  return useQuery<OpenerDescalceSnapshot>(
    OPENER_DESCALCE_KEY,
    fetchOpenerDescalce,
    {
      refetchInterval: pushActive
        ? DESCALCE_POLL_MS_PUSH_ACTIVE
        : DESCALCE_POLL_MS_NO_PUSH,
      refetchOnWindowFocus: true,
      keepPreviousData: true,
      retry: false,
      // Mirror of the monotonic guard the socket applies on its own writes.
      // A REST fetch started before a pushed frame landed still resolves
      // afterwards, and react-query would dispatch its own success and walk
      // the snapshot BACKWARDS — rows that just appeared would vanish until
      // the next sweep. Returning `true` here makes react-query keep the
      // newer cached object instead of replacing it.
      isDataEqual: (oldData, newData) => {
        if (!oldData || !newData) return false;
        if (
          typeof oldData.computedAt !== 'number' ||
          typeof newData.computedAt !== 'number'
        ) {
          return false;
        }
        // Escape hatches FIRST — both cover the same failure, a cached
        // snapshot the backend can never beat, which would freeze the table
        // for the life of the tab. The banner would say Stale (the safe
        // direction), but it would never self-heal.
        //
        //  · the cache is past its own freshness horizon — a producer that
        //    restarted on a clock-skewed node keeps stamping into the past;
        //  · the cache is stamped in the FUTURE beyond any plausible skew —
        //    the mirror case, and the one a horizon check alone cannot see,
        //    because a future stamp never looks old.
        const now = Date.now();
        if (now - oldData.computedAt > oldData.staleMs) return false;
        if (oldData.computedAt - now > CLOCK_SKEW_ALLOWANCE_MS) return false;

        // `>`, not `>=`: at an identical `computedAt` the two snapshots are
        // genuinely different objects and the newer arrival is the one to keep.
        return oldData.computedAt > newData.computedAt;
      }
    }
  );
}
